import { useEffect, useRef, useState, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Upload,
  Play,
  Pause,
  SkipBack,
  SkipForward,
  Trash2,
  Music,
  Loader2,
  Mic,
  Globe,
  Lock,
  Check,
  Search,
  Plus,
  ExternalLink,
} from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import SpotifyImportDialog from "@/components/SpotifyImportDialog";

/**
 * PlaylistsV2 — customer library + artist onboarding + continuous playback.
 *
 * Uses Cloudflare R2 for storage. Uploads bypass the backend by using a
 * pre-signed PUT URL (browser → R2 directly). Playback uses short-lived
 * signed GET URLs so files stay private in the bucket.
 */
export default function PlaylistsV2() {
  const { user } = useAuth();
  const [tracks, setTracks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [artist, setArtist] = useState(null);
  const [showArtistForm, setShowArtistForm] = useState(false);
  const fileRef = useRef(null);

  // Player state
  const [queue, setQueue] = useState([]);      // ordered track ids
  const [nowPlaying, setNowPlaying] = useState(null); // { track, streamUrl }
  const [playing, setPlaying] = useState(false);
  const audioRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [t, a] = await Promise.all([
        api.get("/playlists/tracks/mine"),
        api.get("/artists/mine"),
      ]);
      setTracks(t.data.items || []);
      setArtist(a.data.artist || null);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (user) load(); }, [user, load]);

  // Sync audio events → playing state + auto-advance
  useEffect(() => {
    const el = audioRef.current;
    if (!el) return;
    const onEnd = () => nextInQueue();
    const onPlay = () => setPlaying(true);
    const onPause = () => setPlaying(false);
    el.addEventListener("ended", onEnd);
    el.addEventListener("play", onPlay);
    el.addEventListener("pause", onPause);
    return () => {
      el.removeEventListener("ended", onEnd);
      el.removeEventListener("play", onPlay);
      el.removeEventListener("pause", onPause);
    };
  }, [nowPlaying]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleFile = async (file) => {
    if (!file) return;
    if (file.size > 25 * 1024 * 1024) {
      toast.error("File exceeds 25MB limit");
      return;
    }
    if (!/^(audio\/(mpeg|mp3|mp4|aac|wav|x-wav))$/i.test(file.type)) {
      toast.error("Must be MP3, WAV, or AAC");
      return;
    }
    setUploading(true);
    setUploadProgress(0);
    try {
      // 1) Get pre-signed URL
      const { data: pre } = await api.post("/playlists/tracks/upload-url", {
        filename: file.name,
        content_type: file.type,
        size_bytes: file.size,
      });

      // 2) Upload directly to R2 with progress
      await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open("PUT", pre.upload_url, true);
        xhr.setRequestHeader("Content-Type", file.type);
        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) {
            setUploadProgress(Math.round((e.loaded / e.total) * 100));
          }
        };
        xhr.onload = () => (xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error(`R2 upload failed: ${xhr.status}`)));
        xhr.onerror = () => reject(new Error("Network error uploading to R2"));
        xhr.send(file);
      });

      // 3) Try to sniff duration from the file client-side before registering
      const duration = await sniffDurationSeconds(file);

      // 4) Register in Mongo
      const title = file.name.replace(/\.[^.]+$/, "");
      await api.post("/playlists/tracks", {
        r2_key: pre.key,
        title,
        artist: user?.name || "",
        duration_seconds: duration,
        is_public: false, // owner can toggle after upload
      });

      toast.success(`"${title}" uploaded`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setUploading(false);
      setUploadProgress(0);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const playTrack = async (t, autoAdvanceIds = null) => {
    try {
      const { data } = await api.get(`/playlists/tracks/${t.id}/stream-url`);
      setNowPlaying({ track: data.track, streamUrl: data.stream_url });
      if (autoAdvanceIds) setQueue(autoAdvanceIds);
      // Autoplay after the src changes
      setTimeout(() => {
        if (audioRef.current) {
          audioRef.current.play().catch(() => {});
        }
      }, 100);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const nextInQueue = () => {
    if (!nowPlaying || queue.length === 0) return setPlaying(false);
    const idx = queue.findIndex((id) => id === nowPlaying.track.id);
    const nextId = queue[idx + 1];
    if (!nextId) {
      setPlaying(false);
      return;
    }
    const nextTrack = tracks.find((t) => t.id === nextId);
    if (nextTrack) playTrack(nextTrack, queue);
  };

  const prevInQueue = () => {
    if (!nowPlaying) return;
    const idx = queue.findIndex((id) => id === nowPlaying.track.id);
    const prevId = queue[idx - 1];
    if (!prevId) return;
    const prevTrack = tracks.find((t) => t.id === prevId);
    if (prevTrack) playTrack(prevTrack, queue);
  };

  const playAll = () => {
    if (tracks.length === 0) return;
    const ids = tracks.map((t) => t.id);
    playTrack(tracks[0], ids);
  };

  const removeTrack = async (t) => {
    if (!confirm(`Delete "${t.title}"?`)) return;
    try {
      await api.delete(`/playlists/tracks/${t.id}`);
      if (nowPlaying?.track.id === t.id) setNowPlaying(null);
      toast.success("Track deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const submitArtist = async (e) => {
    e.preventDefault();
    const fd = new FormData(e.target);
    try {
      await api.post("/artists/apply", {
        stage_name: fd.get("stage_name"),
        bio: fd.get("bio"),
        website: fd.get("website"),
        spotify_url: fd.get("spotify_url"),
        apple_music_url: fd.get("apple_music_url"),
        bandcamp_url: fd.get("bandcamp_url"),
      });
      toast.success(artist ? "Artist profile updated" : "Application submitted — awaiting admin approval");
      setShowArtistForm(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  if (!user) {
    return (
      <div className="max-w-2xl mx-auto text-center py-20 text-[#5C4E48]">
        <Music size={32} className="mx-auto text-[#B31E1E] mb-3" />
        <p>Sign in to build your library.</p>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-6 md:px-10 py-10 pb-32" data-testid="playlists-v2">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <span className="inline-flex items-center gap-1.5 rounded-full bg-[#F9E4E1] text-[#B31E1E] border border-[#F0C9C4] px-3 py-1 text-[11px] font-semibold tracking-wide">
            <Music size={11} /> Your library
          </span>
          <h1 className="mt-3 text-4xl md:text-5xl font-black tracking-tight text-[#1B1614]">
            Playlists
          </h1>
          <p className="mt-2 text-[15px] text-[#5C4E48]">
            Upload your own MP3s or link to fan pages. Continuous playback across your library.
          </p>
        </div>

        <div className="flex gap-2 flex-wrap">
          <input
            ref={fileRef}
            type="file"
            accept="audio/mpeg,audio/mp3,audio/mp4,audio/aac,audio/wav,audio/x-wav,.mp3,.wav,.aac,.m4a"
            className="hidden"
            data-testid="mp3-file-input"
            onChange={(e) => handleFile(e.target.files?.[0])}
          />
          <Button
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            className="rounded-lg bg-[#B31E1E] hover:bg-[#8F1717] text-white h-11 px-5 font-semibold"
            data-testid="mp3-upload-button"
          >
            {uploading ? (
              <>
                <Loader2 size={16} className="mr-1.5 animate-spin" />
                Uploading… {uploadProgress}%
              </>
            ) : (
              <>
                <Upload size={16} className="mr-1.5" /> Upload MP3
              </>
            )}
          </Button>
          <SpotifyImportDialog onAdded={load} />
          {tracks.length > 0 && (
            <Button
              variant="outline"
              onClick={playAll}
              className="rounded-lg border-[#EAE3DE] bg-white hover:bg-[#FAF5F2] h-11"
              data-testid="play-all-button"
            >
              <Play size={16} className="mr-1.5" /> Play all
            </Button>
          )}
        </div>
      </div>

      {/* Artist card */}
      <Card className="mt-8 rounded-2xl border border-[#EAE3DE] bg-white p-5" data-testid="artist-card">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <Mic size={16} className="text-[#B31E1E]" />
              <span className="font-semibold text-[#1B1614]">
                {artist ? artist.stage_name : "Become an artist"}
              </span>
              {artist && artist.approved && (
                <span className="inline-flex items-center gap-1 text-xs bg-emerald-100 text-emerald-700 rounded-full px-2 py-0.5 font-mono">
                  <Check size={11} /> Approved
                </span>
              )}
              {artist && !artist.approved && (
                <span className="text-xs bg-amber-100 text-amber-800 rounded-full px-2 py-0.5 font-mono">
                  Pending review
                </span>
              )}
            </div>
            <p className="mt-1 text-sm text-[#5C4E48] max-w-xl">
              {artist
                ? artist.bio || "No bio yet — add one to help fans find you."
                : "Get a public artist page on Servrevu. Fans can browse your catalog, favourite tracks, and follow you."}
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              className="rounded-lg border-[#EAE3DE]"
              onClick={() => setShowArtistForm((v) => !v)}
              data-testid="artist-toggle-form"
            >
              {artist ? "Edit profile" : "Apply"}
            </Button>
            {artist && artist.approved && (
              <Link to={`/artists/${artist.id}`}>
                <Button size="sm" variant="outline" className="rounded-lg border-[#EAE3DE]" data-testid="artist-view-page">
                  <Globe size={13} className="mr-1" /> View page
                </Button>
              </Link>
            )}
          </div>
        </div>

        {showArtistForm && (
          <form className="mt-4 space-y-3" onSubmit={submitArtist}>
            <div>
              <Label className="text-xs">Stage name</Label>
              <Input name="stage_name" required defaultValue={artist?.stage_name || ""} className="rounded-lg mt-1" data-testid="artist-stage-name" />
            </div>
            <div>
              <Label className="text-xs">Short bio</Label>
              <Textarea name="bio" rows={3} defaultValue={artist?.bio || ""} className="rounded-lg mt-1" data-testid="artist-bio" />
            </div>
            <div>
              <Label className="text-xs">Website / social (optional)</Label>
              <Input name="website" type="url" defaultValue={artist?.website || ""} className="rounded-lg mt-1" data-testid="artist-website" />
            </div>
            <div>
              <Label className="text-xs">Spotify artist or playlist URL (optional)</Label>
              <Input
                name="spotify_url"
                type="url"
                placeholder="https://open.spotify.com/artist/… or /playlist/…"
                defaultValue={artist?.spotify_url || ""}
                className="rounded-lg mt-1"
                data-testid="artist-spotify-url"
              />
              <p className="text-[11px] text-[#8B7B73] mt-1">Renders as an embedded Spotify player on your public band page.</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Apple Music (optional)</Label>
                <Input name="apple_music_url" type="url" defaultValue={artist?.apple_music_url || ""} className="rounded-lg mt-1" data-testid="artist-apple-music-url" />
              </div>
              <div>
                <Label className="text-xs">Bandcamp (optional)</Label>
                <Input name="bandcamp_url" type="url" defaultValue={artist?.bandcamp_url || ""} className="rounded-lg mt-1" data-testid="artist-bandcamp-url" />
              </div>
            </div>
            <Button type="submit" className="rounded-lg bg-[#B31E1E] hover:bg-[#8F1717] text-white" data-testid="artist-submit">
              {artist ? "Save" : "Apply for approval"}
            </Button>
          </form>
        )}
      </Card>

      {/* Track list */}
      <div className="mt-8">
        <h2 className="text-xs font-semibold uppercase tracking-wider text-[#5C4E48] mb-3">
          Your uploads ({tracks.length})
        </h2>
        {loading ? (
          <p className="text-sm text-[#8B7B73]">Loading…</p>
        ) : tracks.length === 0 ? (
          <Card className="rounded-2xl border-dashed border-[#EAE3DE] p-10 text-center bg-white">
            <Music size={30} className="mx-auto text-[#B31E1E] mb-3" />
            <p className="text-[#1B1614] font-semibold">No tracks yet</p>
            <p className="text-sm text-[#5C4E48] mt-1">Click <strong>Upload MP3</strong> above to add your first track.</p>
          </Card>
        ) : (
          <ul className="space-y-1.5" data-testid="tracks-list">
            {tracks.map((t) => {
              const isCurrent = nowPlaying?.track.id === t.id;
              return (
                <li
                  key={t.id}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-lg border ${
                    isCurrent
                      ? "border-[#B31E1E]/40 bg-[#F9E4E1]/40"
                      : "border-[#EAE3DE] bg-white hover:bg-[#FAF5F2]"
                  } transition-colors`}
                  data-testid={`track-${t.id}`}
                >
                  <Button
                    size="sm"
                    variant="ghost"
                    className="rounded-full h-9 w-9 p-0 hover:bg-[#B31E1E] hover:text-white"
                    onClick={() => (isCurrent && playing ? audioRef.current?.pause() : playTrack(t, tracks.map((x) => x.id)))}
                    data-testid={`track-play-${t.id}`}
                  >
                    {isCurrent && playing ? <Pause size={16} /> : <Play size={16} />}
                  </Button>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-[#1B1614] truncate">{t.title}</div>
                    <div className="text-xs text-[#8B7B73] truncate">
                      {t.artist || t.owner_name}
                      {t.album ? ` · ${t.album}` : ""}
                      {t.duration_seconds ? ` · ${formatDuration(t.duration_seconds)}` : ""}
                      <span className="ml-2">
                        {t.is_public ? (
                          <span className="inline-flex items-center gap-1 text-emerald-700"><Globe size={10} /> Public</span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-[#8B7B73]"><Lock size={10} /> Private</span>
                        )}
                      </span>
                    </div>
                  </div>
                  <span className="text-xs font-mono text-[#8B7B73] hidden sm:inline">
                    {t.play_count} plays
                  </span>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-[#8B7B73] hover:text-red-600 hover:bg-red-50 rounded-full h-8 w-8 p-0"
                    onClick={() => removeTrack(t)}
                    data-testid={`track-delete-${t.id}`}
                  >
                    <Trash2 size={14} />
                  </Button>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* Persistent player bar */}
      {nowPlaying && (
        <div
          className="fixed bottom-0 left-0 right-0 z-40 bg-white/95 backdrop-blur-md border-t border-[#EAE3DE] shadow-[0_-4px_20px_-8px_rgba(0,0,0,0.10)]"
          data-testid="player-bar"
        >
          <div className="max-w-5xl mx-auto px-6 md:px-10 py-3 flex items-center gap-4">
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-[#1B1614] truncate">{nowPlaying.track.title}</div>
              <div className="text-xs text-[#8B7B73] truncate">{nowPlaying.track.artist || nowPlaying.track.owner_name}</div>
            </div>
            <div className="flex items-center gap-1">
              <Button size="sm" variant="ghost" onClick={prevInQueue} className="rounded-full h-9 w-9 p-0" data-testid="player-prev">
                <SkipBack size={16} />
              </Button>
              <Button
                size="sm"
                onClick={() => (playing ? audioRef.current?.pause() : audioRef.current?.play())}
                className="rounded-full h-10 w-10 p-0 bg-[#B31E1E] hover:bg-[#8F1717] text-white"
                data-testid="player-play-pause"
              >
                {playing ? <Pause size={16} /> : <Play size={16} />}
              </Button>
              <Button size="sm" variant="ghost" onClick={nextInQueue} className="rounded-full h-9 w-9 p-0" data-testid="player-next">
                <SkipForward size={16} />
              </Button>
            </div>
            <audio
              ref={audioRef}
              src={nowPlaying.streamUrl}
              controls
              className="hidden sm:block flex-shrink-0 max-w-[300px]"
              data-testid="player-audio"
            />
          </div>
        </div>
      )}
    </div>
  );
}

/** Format duration seconds → "3:24". */
function formatDuration(s) {
  const total = Math.max(0, Math.floor(s));
  const m = Math.floor(total / 60);
  const sec = String(total % 60).padStart(2, "0");
  return `${m}:${sec}`;
}

/** Read the audio duration client-side before uploading — small nicety so
 * the track card shows "3:24" without needing a separate metadata worker. */
function sniffDurationSeconds(file) {
  return new Promise((resolve) => {
    try {
      const url = URL.createObjectURL(file);
      const el = new Audio();
      el.src = url;
      el.onloadedmetadata = () => {
        const d = Math.round(el.duration || 0);
        URL.revokeObjectURL(url);
        resolve(d || null);
      };
      el.onerror = () => {
        URL.revokeObjectURL(url);
        resolve(null);
      };
    } catch {
      resolve(null);
    }
  });
}
